import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbDropdownModule, NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { BasePageComponent } from '../../../_shared/components/page/base-page.component';
import { AccountFilterComponent } from '../../../_shared/components/filters/account-filter/account-filter.component';
import { DateFilterComponent } from '../../../_shared/components/filters/date-filter/date-filter.component';
import { AccountFilterData, DateIntervalFilterData, WidgetFilterData } from '../../../_shared/models';
import { DateInterval } from '../../../_shared/enums';
import { FilterService } from '../../../_shared/services/filter.service';
import { isEmptyObject } from '../../../_shared/helpers/object.helper';
import { TotalClaimsComponent } from './components/widgets/total-claims/total-claims.component';
import { TotalAmountClaimedComponent } from './components/widgets/total-amount-claimed/total-amount-claimed.component';
import { ActiveClaimComponent } from './components/widgets/active-claim/active-claim.component';
import { RejectedComponent } from './components/widgets/rejected/rejected.component';
import { PaginationComponent } from '../../../_shared/components/pagination/pagination.component';
import { EmptyStateComponent } from '../../../_shared/components/states/empty-state/empty-state.component';
import { InsuranceClaimsService } from './services/insurance-claims.service';
import { InsuranceClaimsRequest } from './models/insurance-claim-request.model';
import { InsuranceClaim } from './models/insurance-claim-response.model';
import { InsuranceClaimStatus, InsuranceClaimStatusLabelKeys } from './enums/insurance-claim-status.enum';
import { Subject } from 'rxjs';
import { ErrorHandlerService } from '../../../_shared/services/error-handler.service';
import { CurrencyHelper } from '../../../_shared/helpers/currency/currency-map.helper';
import { EditInsuranceClaimModalComponent } from './components/modals/edit-insurance-claim-modal/edit-insurance-claim-modal.component';
import { UpdateClaimModalComponent } from './components/modals/update-claim-modal/update-claim-modal.component';
import { SearchComponent } from '../../../_shared/components/filters/search/search.component';
import { InsuranceClaimStatusFilterComponent } from './components/insurance-claim-status-filter/insurance-claim-status-filter.component';
import { AttachmentsPillComponent } from '../accidents-and-incidents/components/attachments-pill/attachments-pill.component';
import { PhotosVideosViewerModalComponent } from '../../_shared/components/modals/photos-videos-viewer-modal/photos-videos-viewer-modal.component';

@Component({
  selector: 'app-insurance-claims',
  standalone: true,
  imports: [
    CommonModule,
    NgbDropdownModule,
    AccountFilterComponent,
    DateFilterComponent,
    TotalClaimsComponent,
    TotalAmountClaimedComponent,
    ActiveClaimComponent,
    RejectedComponent,
    PaginationComponent,
    EmptyStateComponent,
    SearchComponent,
    InsuranceClaimStatusFilterComponent,
    AttachmentsPillComponent,
    TranslocoDirective,
    TranslocoPipe
  ],
  templateUrl: './insurance-claims.component.html',
  styleUrl: './insurance-claims.component.scss'
})
export class InsuranceClaimsComponent extends BasePageComponent {
  private filterService: FilterService = inject(FilterService);
  private claimsService: InsuranceClaimsService = inject(InsuranceClaimsService);
  private modalService: NgbModal = inject(NgbModal);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private readonly transloco: TranslocoService = inject(TranslocoService);
  private cancelSubject: Subject<void> = new Subject<void>();

  currencySymbol: string = CurrencyHelper.getCurrencySymbol(this.authService.getUserSettings()?.currencyCode ?? 'ZAR');
  isLoading: boolean = true;
  accountFilter: AccountFilterData;
  dateFilter: DateIntervalFilterData;
  defaultInterval: DateInterval = DateInterval.Month;
  disabledIntervals: DateInterval[] = [DateInterval.Half];

  statusId: number | null = null;
  searchTerm: string = '';

  // Table properties
  claims: InsuranceClaim[] = [];
  page: number = 1;
  pageSize: number = 10;
  totalPages: number = 0;
  totalCount: number = 0;
  insuranceClaimStatus = InsuranceClaimStatus;

  constructor() {
    super();
    this.accountFilter = {} as AccountFilterData;
    this.dateFilter = {} as DateIntervalFilterData;
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  onAccountFilterChange(filter: AccountFilterData): void {
    this.accountFilter = filter;
    this.updateFilter();
  }

  onDateFilterChange(filter: DateIntervalFilterData): void {
    this.dateFilter = filter;
    this.updateFilter();
  }

  updateFilter(): void {
    if (!isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      this.filterService.updateFilter({
        ...this.dateFilter,
        ...this.accountFilter
      } as WidgetFilterData);

      this.page = 1;
      this.claims = [];
      this.updatePageFilterState();
      this.loadClaims();
    }
  }

  async onStatusChanged(statusId: number | null): Promise<void> {
    this.statusId = statusId;
    this.page = 1;
    this.claims = [];
    this.updatePageFilterState();

    await this.loadClaims();
  }

  async onSearchTermUpdated(searchTerm: string): Promise<void> {
    this.searchTerm = searchTerm;
    this.page = 1;
    this.claims = [];
    this.updatePageFilterState();

    await this.loadClaims();
  }

  // Table methods
  private async loadClaims(): Promise<void> {
    if (!this.dateFilter.startDate || !this.dateFilter.endDate) {
      return;
    }

    this.isLoading = true;

    try {
      this.cancelSubject.next();

      const request: InsuranceClaimsRequest = {
        accountIds: this.accountFilter.accountIds ?? [],
        timeFrom: this.dateFilter.startDate,
        timeTo: this.dateFilter.endDate,
        interval: this.dateFilter.interval,
        page: this.page,
        pageSize: this.pageSize,
        searchTerm: this.searchTerm || undefined,
        statusIds: this.statusId ? [this.statusId] : []
      };

      const response = await this.claimsService.getInsuranceClaims(request, this.cancelSubject);
      this.claims = response.data?.results ?? [];
      this.totalCount = response.data?.totalCount ?? 0;
      this.totalPages = response.data?.totalPages ?? 0;

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  get showingCount(): number {
    const start = (this.page - 1) * this.pageSize;
    const end = Math.min(start + this.pageSize, this.totalCount);
    return end;
  }

  goToPage(page: number): void {
    this.page = page;
    this.loadClaims();
  }

  onExport(): void {
    console.log('Export clicked');
  }

  onEdit(claim: InsuranceClaim): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      windowClass: 'edit-claim-modal',
      beforeDismiss: () => !modalRef.componentInstance.isSubmitting
    };

    const modalRef = this.modalService.open(EditInsuranceClaimModalComponent, modalOptions);
    modalRef.componentInstance.claim = claim;

    modalRef.result.then((result) => {
      if (result) {
        this.refreshAfterClaimChange();
      }
    });
  }

  onUpdateClaim(claim: InsuranceClaim): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static'
    };

    const modalRef = this.modalService.open(UpdateClaimModalComponent, modalOptions);
    modalRef.componentInstance.claim = claim;

    modalRef.result.then((result) => {
      if (result) {
        this.refreshAfterClaimChange();
      }
    });
  }

  private refreshAfterClaimChange(): void {
    this.filterService.updateFilter(this.filterService.filter);
    this.loadClaims();
  }

  getAttachmentCount(claim: InsuranceClaim): number {
    return claim.attachments?.length ?? 0;
  }

  canViewAttachments(claim: InsuranceClaim): boolean {
    return this.getAttachmentCount(claim) > 0;
  }

  onViewAttachments(claim: InsuranceClaim): void {
    if (!this.canViewAttachments(claim)) {
      return;
    }

    const modalOptions: NgbModalOptions = {
      centered: true,
      windowClass: 'photos-videos-viewer-modal'
    };

    const modalRef = this.modalService.open(PhotosVideosViewerModalComponent, modalOptions);
    modalRef.componentInstance.title = this.transloco.translate('accidents.claims.documentsModalTitle');
    modalRef.componentInstance.contextLabel = claim.assetName;
    modalRef.componentInstance.reportedBy = claim.createdBy;
    modalRef.componentInstance.reportedAt = claim.dateFiled;
    modalRef.componentInstance.items = claim.attachments ?? [];
  }

  getStatusLabelKey(status: string | null): string | null {
    return InsuranceClaimStatusLabelKeys[status as InsuranceClaimStatus] ?? null;
  }

  getStatusClass(status: string | null): string {
    switch (status) {
      case InsuranceClaimStatus.Filed:
        return 'status-filed';
      case InsuranceClaimStatus.UnderReview:
        return 'status-under-review';
      case InsuranceClaimStatus.Rejected:
        return 'status-rejected';
      case InsuranceClaimStatus.InformationRequested:
        return 'status-information-requested';
      case InsuranceClaimStatus.Paid:
        return 'status-paid';
      default:
        return '';
    }
  }

  formatSettledAmount(claim: InsuranceClaim): string {
    if (claim.settledAmount === null) return '-';
    const amount = claim.settledAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${this.currencySymbol} ${amount}`;
  }

  protected override onRefresh(): void { }

  protected override shouldRefresh(): boolean { return false; }

  protected override getFilters(): any {
    return {
      ...this.dateFilter,
      ...this.accountFilter,
      statusId: this.statusId,
      searchTerm: this.searchTerm
    }
  }

  protected override applyPageFilterState(pageFilter: any): void { }
}